import { useState, useRef, useEffect } from 'react';
import { api, User } from '../api';
import { getErrorMessage } from '../../../shared/src';
import { StatusBadge } from './ui';
import { InviteUserModal } from './modals/InviteUserModal';

// Available roles - easy to extend in the future
const AVAILABLE_ROLES = [
  { value: 'user', label: 'Member', description: 'Standard access' },
  { value: 'org_admin', label: 'Admin', description: 'Full organization access' },
] as const;

type RoleValue = typeof AVAILABLE_ROLES[number]['value'];

interface RoleSelectorProps {
  currentRole: string;
  userId: string;
  isUpdating: boolean;
  onRoleChange: (userId: string, newRole: RoleValue) => void;
}

function RoleSelector({ currentRole, userId, isUpdating, onRoleChange }: RoleSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentRoleInfo = AVAILABLE_ROLES.find(r => r.value === currentRole) || AVAILABLE_ROLES[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (role: RoleValue) => {
    if (role !== currentRole) {
      onRoleChange(userId, role);
    }
    setIsOpen(false);
  };

  return (
    <div className="role-selector" ref={dropdownRef}>
      <button
        className={`role-selector-trigger ${currentRole === 'org_admin' ? 'admin' : 'user'}`}
        onClick={() => !isUpdating && setIsOpen(!isOpen)}
        disabled={isUpdating}
      >
        <span className="role-label">{isUpdating ? 'Updating...' : currentRoleInfo.label}</span>
        <svg className="chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {isOpen && (
        <div className="role-selector-dropdown">
          {AVAILABLE_ROLES.map((role) => (
            <button
              key={role.value}
              className={`role-option ${role.value === currentRole ? 'selected' : ''}`}
              onClick={() => handleSelect(role.value)}
            >
              <div className="role-option-content">
                <span className="role-option-label">{role.label}</span>
                <span className="role-option-description">{role.description}</span>
              </div>
              {role.value === currentRole && (
                <svg className="check-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M11.5 4L5.5 10L2.5 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface UsersTabProps {
  orgId: string;
  users: User[];
  setUsers: (users: User[]) => void;
  authMethod: 'sso' | 'password';
}

export function UsersTab({ orgId, users, setUsers, authMethod }: UsersTabProps) {
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleRoleChange = async (userId: string, newRole: RoleValue) => {
    setUpdatingUserId(userId);
    try {
      const result = await api.updateUserRole(orgId, userId, newRole);
      setUsers(users.map((u) => (u.id === userId ? result.user : u)));
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to update role: ${getErrorMessage(err)}` });
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleInviteSuccess = (newUser: User) => {
    setUsers([...users, newUser]);
    setShowInviteModal(false);
  };

  const handleResendInvitation = async (userId: string) => {
    if (!window.confirm('Resend invitation email to this user?')) return;

    setActionInProgress(`resend-${userId}`);
    try {
      await api.resendInvitation(orgId, userId);
      setActionMessage({ type: 'success', text: 'Invitation email sent successfully' });
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to resend invitation: ${getErrorMessage(err)}` });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail: string) => {
    if (!window.confirm(`Remove ${userEmail} from this organization? This action cannot be undone.`)) return;

    setActionInProgress(`delete-${userId}`);
    try {
      await api.deleteUser(orgId, userId);
      setUsers(users.filter((u) => u.id !== userId));
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to remove user: ${getErrorMessage(err)}` });
    } finally {
      setActionInProgress(null);
    }
  };

  const canInviteUsers = authMethod === 'password';

  return (
    <div className="tab-content">
      {canInviteUsers && (
        <div className="users-header">
          <button className="btn-primary" onClick={() => setShowInviteModal(true)}>
            Invite User
          </button>
        </div>
      )}

      {actionMessage && (
        <div className={actionMessage.type === 'success' ? 'success-message' : 'error-message'}>
          {actionMessage.text}
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Role</th>
            <th>Status</th>
            <th>Last Login</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>{user.email}</td>
              <td>{user.first_name} {user.last_name}</td>
              <td>
                <RoleSelector
                  currentRole={user.role}
                  userId={user.id}
                  isUpdating={updatingUserId === user.id}
                  onRoleChange={handleRoleChange}
                />
              </td>
              <td><StatusBadge status={user.status} /></td>
              <td>{user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Never'}</td>
              <td className="actions-cell">
                {actionInProgress?.includes(user.id) ? (
                  <span className="updating">Processing...</span>
                ) : (
                  <div className="action-buttons">
                    {/* Resend invitation for invited users (password auth only) */}
                    {canInviteUsers && user.status === 'invited' && (
                      <button
                        className="btn-small btn-secondary"
                        onClick={() => handleResendInvitation(user.id)}
                      >
                        Resend Invite
                      </button>
                    )}

                    {/* Delete user (password auth only) */}
                    {canInviteUsers && (
                      <button
                        className="btn-small btn-danger"
                        onClick={() => handleDeleteUser(user.id, user.email)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
          {users.length === 0 && (
            <tr>
              <td colSpan={6} className="empty-state">
                {canInviteUsers
                  ? 'No users yet. Click "Invite User" to add the first user.'
                  : 'No users yet. Users will appear here after their first SSO login.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showInviteModal && (
        <InviteUserModal
          orgId={orgId}
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInviteSuccess}
        />
      )}

      <style>{`
        .users-header {
          margin-bottom: 16px;
        }

        .actions-cell {
          min-width: 140px;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .btn-small {
          padding: 4px 10px;
          font-size: 13px;
          border-radius: 4px;
          cursor: pointer;
          border: none;
          transition: background-color 0.2s;
        }

        .btn-secondary {
          background-color: #e0e0e0;
          color: #333;
        }

        .btn-secondary:hover {
          background-color: #d0d0d0;
        }

        .btn-danger {
          background-color: #dc3545;
          color: white;
        }

        .btn-danger:hover {
          background-color: #c82333;
        }

        .updating {
          color: #666;
          font-style: italic;
        }

        /* Role Selector Dropdown */
        .role-selector {
          position: relative;
          display: inline-block;
        }

        .role-selector-trigger {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          font-size: 13px;
          font-weight: 500;
          border: 1px solid transparent;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.15s ease;
          background: hsl(var(--muted));
          color: hsl(var(--muted-foreground));
        }

        .role-selector-trigger.admin {
          background: hsl(var(--accent) / 0.15);
          color: hsl(165 54% 35%);
        }

        .role-selector-trigger:hover:not(:disabled) {
          border-color: hsl(var(--border));
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .role-selector-trigger:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .role-selector-trigger .chevron {
          transition: transform 0.2s;
        }

        .role-selector:has(.role-selector-dropdown) .role-selector-trigger .chevron {
          transform: rotate(180deg);
        }

        .role-selector-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          min-width: 200px;
          background: white;
          border: 1px solid hsl(var(--border));
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 100;
          overflow: hidden;
        }

        .role-option {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 10px 12px;
          border: none;
          background: none;
          cursor: pointer;
          text-align: left;
          transition: background-color 0.15s;
        }

        .role-option:hover {
          background: hsl(var(--muted) / 0.5);
        }

        .role-option.selected {
          background: hsl(var(--accent) / 0.08);
        }

        .role-option-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .role-option-label {
          font-size: 14px;
          font-weight: 500;
          color: hsl(var(--foreground));
        }

        .role-option-description {
          font-size: 12px;
          color: hsl(var(--muted-foreground));
        }

        .check-icon {
          color: hsl(var(--accent));
          flex-shrink: 0;
        }
      `}</style>
    </div>
  );
}
